import React from "react";
import { FiUsers, FiArrowUp, FiArrowDown, FiTrendingUp } from "react-icons/fi";
import { SectionHeader } from "../../../components/Shared";
import { MiniBar } from "./facilityHelpers";

const floorStatusColor = (s) =>
  s === "High" ? "#ef4444" : s === "Low" ? "#64748b" : "#10b981";

const floorStatusBg = (s) =>
  s === "High" ? "#fef2f2" : s === "Low" ? "#f8fafc" : "#f0fdf4";

const OccupancyPanel = ({ d }) => {
  const occ = d.occupancy;
  const maxHourly = Math.max(...occ.hourlyTrend.map(h => h.v));
  const maxWeekly = Math.max(...occ.weeklyAvg.map(w => w.v));
  const utilizationPct = Math.round((occ.currentOccupancy / occ.totalCapacity) * 100);

  return (
    <div>
      {/* Stat cards */}
      <div className="fm-occ-stat-row">
        <div className="fm-occ-stat-card fm-occ-main">
          <div className="fm-occ-stat-icon" style={{ background: "#667eea18", color: "#667eea" }}>
            <FiUsers size={24} />
          </div>
          <div>
            <div className="fm-occ-stat-val" style={{ color: "#667eea" }}>{occ.currentOccupancy}</div>
            <div className="fm-occ-stat-lbl">Current Occupancy</div>
            <div className="fm-occ-stat-sub">{utilizationPct}% of {occ.totalCapacity} capacity</div>
          </div>
        </div>

        <div className="fm-occ-stat-card">
          <div className="fm-occ-stat-icon" style={{ background: "#10b98118", color: "#10b981" }}>
            <FiArrowUp size={22} />
          </div>
          <div>
            <div className="fm-occ-stat-val" style={{ color: "#10b981" }}>{occ.entryToday}</div>
            <div className="fm-occ-stat-lbl">Entries Today</div>
            <div className="fm-occ-stat-sub">Via all access points</div>
          </div>
        </div>

        <div className="fm-occ-stat-card">
          <div className="fm-occ-stat-icon" style={{ background: "#f59e0b18", color: "#f59e0b" }}>
            <FiArrowDown size={22} />
          </div>
          <div>
            <div className="fm-occ-stat-val" style={{ color: "#f59e0b" }}>{occ.exitToday}</div>
            <div className="fm-occ-stat-lbl">Exits Today</div>
            <div className="fm-occ-stat-sub">Still inside: {occ.entryToday - occ.exitToday}</div>
          </div>
        </div>

        <div className="fm-occ-stat-card">
          <div className="fm-occ-stat-icon" style={{ background: "#ef444418", color: "#ef4444" }}>
            <FiTrendingUp size={22} />
          </div>
          <div>
            <div className="fm-occ-stat-val" style={{ color: "#ef4444" }}>{occ.peakToday}</div>
            <div className="fm-occ-stat-lbl">Peak Today</div>
            <div className="fm-occ-stat-sub">At {occ.peakTime}</div>
          </div>
        </div>
      </div>

      {/* Overall utilization bar */}
      <div className="fm-occ-util-bar">
        <div className="fm-occ-util-labels">
          <span>Overall Building Utilization</span>
          <span style={{
            fontWeight: 700,
            color: utilizationPct > 85 ? "#ef4444" : utilizationPct > 70 ? "#f59e0b" : "#10b981",
          }}>
            {utilizationPct}%
          </span>
        </div>
        <MiniBar value={occ.currentOccupancy} max={occ.totalCapacity} height={14}
          color={utilizationPct > 85 ? "#ef4444" : utilizationPct > 70 ? "#f59e0b" : "#10b981"} />
      </div>

      {/* Floor-wise occupancy */}
      <SectionHeader title="Floor-wise Occupancy" />
      <div className="fm-occ-floors-grid">
        {occ.floors.map(fl => {
          const pct = Math.round((fl.current / fl.capacity) * 100);
          const color = floorStatusColor(fl.status);
          return (
            <div key={fl.id} className="fm-occ-floor-card"
              style={{ background: floorStatusBg(fl.status), borderColor: `${color}40` }}>
              <div className="fm-occ-floor-header">
                <span className="fm-occ-floor-name">{fl.name}</span>
                <span className="fm-occ-floor-badge" style={{ background: `${color}18`, color }}>
                  {fl.status}
                </span>
              </div>
              <div className="fm-occ-floor-count" style={{ color }}>
                {fl.current}
                <span className="fm-occ-floor-cap">/ {fl.capacity}</span>
              </div>
              <MiniBar value={fl.current} max={fl.capacity} height={6} color={color} />
              <div className="fm-occ-floor-pct">{pct}% utilized</div>
            </div>
          );
        })}
      </div>

      {/* Charts row */}
      <div className="fm-charts-row">
        {/* Hourly trend */}
        <div className="fm-chart-card">
          <div className="fm-chart-title">Hourly Occupancy Trend – Today</div>
          <div className="fm-bar-chart">
            {occ.hourlyTrend.map((h, i) => (
              <div key={i} className="fm-bar-col">
                <div className="fm-bar-wrap">
                  <div className="fm-bar-fill" style={{
                    height: `${(h.v / maxHourly) * 100}%`,
                    background: h.v === maxHourly ? "#ef4444"
                      : h.v > occ.totalCapacity * 0.75 ? "#f59e0b" : "#667eea",
                  }} />
                </div>
                <div className="fm-bar-label">{h.h}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Weekly average */}
        <div className="fm-chart-card">
          <div className="fm-chart-title">Weekly Average Occupancy</div>
          <div className="fm-bar-chart">
            {occ.weeklyAvg.map((w, i) => (
              <div key={i} className="fm-bar-col">
                <div className="fm-bar-wrap">
                  <div className="fm-bar-fill" style={{
                    height: `${(w.v / maxWeekly) * 100}%`,
                    background: w.v === maxWeekly ? "#ef4444" : "#10b981",
                  }} />
                </div>
                <div className="fm-bar-label">{w.d}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default OccupancyPanel;
